import React, { useState } from 'react';
import { 
  Stack, 
  Text, 
  Group, 
  Button, 
  Select,
  Card,
  Loader,
  Center,
  Alert
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconCalendar, IconDownload, IconAlertCircle } from '@tabler/icons-react';
import { useDailyReport, useWeeklyReports } from '../../../hooks/parent/useDailyReports';
import { DailyReportCard } from './DailyReportCard';
import { notifications } from '@mantine/notifications';

interface DailyReportsListProps {
  childId: string;
}

export const DailyReportsList: React.FC<DailyReportsListProps> = ({ childId }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');

  const dateString = selectedDate?.toISOString().split('T')[0];
  
  const { 
    data: dailyReport, 
    isLoading: isDayLoading,
    error: dayError 
  } = useDailyReport(childId, dateString);

  const { 
    reports: weeklyReports, 
    isLoading: isWeekLoading,
    hasError: weekError 
  } = useWeeklyReports(childId);

  const handleDownload = (_reportId?: string) => {
    notifications.show({
      title: 'Download Started',
      message: 'Your report is being prepared for download',
      color: 'blue',
    });
  };

  const handlePrint = (_reportId?: string) => {
    window.print();
  };

  const handleDownloadWeek = () => {
    notifications.show({
      title: 'Download Started',
      message: 'Weekly reports are being prepared for download',
      color: 'blue',
    });
  };

  const isLoading = viewMode === 'day' ? isDayLoading : isWeekLoading;
  const hasError = viewMode === 'day' ? dayError : weekError;

  return (
    <Stack gap="lg">
      {/* Controls */}
      <Card withBorder p="md">
        <Group justify="space-between" align="flex-end">
          <Group>
            <Select
              label="View Mode"
              value={viewMode}
              onChange={(value) => setViewMode(value as 'day' | 'week')}
              data={[
                { value: 'day', label: 'Single Day' },
                { value: 'week', label: 'Week View' },
              ]}
              style={{ width: 150 }}
            />
            
            {viewMode === 'day' && (
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                maxDate={new Date()}
              />
            )}
          </Group>

          {viewMode === 'week' && weeklyReports.length > 0 && (
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadWeek}
            >
              Download Week
            </Button>
          )}
        </Group>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Center p="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">Loading reports...</Text>
          </Stack>
        </Center>
      ) : hasError ? (
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          color="red" 
          variant="light"
        >
          <Text fw={500}>Error loading reports</Text>
          <Text size="sm">Please try again later or contact support if the issue persists.</Text>
        </Alert>
      ) : viewMode === 'day' ? (
        dailyReport ? (
          <DailyReportCard 
            report={dailyReport} 
            onDownload={() => handleDownload(dailyReport.id)}
            onPrint={() => handlePrint(dailyReport.id)}
          />
        ) : (
          <Card withBorder p="xl">
            <Stack align="center" gap="md">
              <IconCalendar size={48} color="gray" />
              <div style={{ textAlign: 'center' }}>
                <Text size="lg" fw={500}>No report available</Text>
                <Text size="sm" c="dimmed">
                  No daily report for {selectedDate?.toLocaleDateString()}
                </Text>
              </div>
            </Stack>
          </Card>
        )
      ) : (
        <Stack gap="md">
          {weeklyReports.length > 0 ? (
            weeklyReports.map(({ date, report }) => (
              report && (
                <DailyReportCard 
                  key={date} 
                  report={report}
                  onDownload={() => handleDownload(report.id)}
                  onPrint={() => handlePrint(report.id)}
                />
              )
            ))
          ) : (
            <Card withBorder p="xl">
              <Stack align="center" gap="md">
                <IconCalendar size={48} color="gray" />
                <div style={{ textAlign: 'center' }}>
                  <Text size="lg" fw={500}>No reports this week</Text>
                  <Text size="sm" c="dimmed">
                    No daily reports available for the past week
                  </Text>
                </div>
              </Stack>
            </Card>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default DailyReportsList;